import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import { FormModal, Field, FilePicker, inputCls, btnPrimary, btnGhost } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import StatusActionsCard from '../components/workflow/StatusActionsCard';
import ChangesSinceReturnCard from '../components/workflow/ChangesSinceReturnCard';
import WorkflowStatusBadge from '../components/workflow/WorkflowStatusBadge';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import AttachmentsList from '../components/workflow/AttachmentsList';
import EditableAttachments, { useRemovableFiles } from '../components/workflow/EditableAttachments';
import HistoryCard from '../components/workflow/HistoryCard';
import RejectModal from '../components/workflow/RejectModal';
import ReturnCommentsBanner from '../components/workflow/ReturnCommentsBanner';
import FieldCommentAnchor from '../components/workflow/FieldCommentAnchor';
import { useFieldComments, FieldCommentsProvider, FieldCommentInline } from '../components/workflow/fieldComments';
import WorkflowRefLink from '../components/shared/WorkflowRefLink';
import { ScreeningCompaniesList } from '../components/workflow/ScreeningCompanies';
import { ProfileDetailItem } from '../components/workflow/ProfileDetail';
import { PROFILE_CHANGE_LABELS, pieText } from '../utils/engagementProfile';
import { profileSplitOf } from '../components/shared/EngagementFormFields';
import { ScreeningAnswersView, screeningAnswersToFields } from '../components/shared/ScreeningFields';
import {
  ScreeningRequestFormFields, emptyScreeningForm, screeningRequestGaps, screeningRequestFormData,
} from './WorkflowScreeningNew';
import { ScreeningOriginBadge } from './WorkflowScreening';
import {
  actionPrereqLabel, actionPrereqHint, detailLabel,
  subsectionTitle, subsectionTitleBar,
} from '../components/workflow/constants';
import { formatDateTime } from '../utils/dateFormat';
import NowName from '../components/shared/NowName';
import { fetchJSON, API_BASE_URL } from '../utils/api';

// Screening — detail page (docs/modules/workflow-screening.md).
// QM completes the request by attaching the screening PDF (its file picker
// sits next to the green button — StatusActionsCard.beforeActions). A request
// filed directly may be returned / modified / deleted; one opened by a
// registration has no requester side — QM completes it, full stop.

// Shared with the Management screening record's history.
export const SCREENING_CHANGE_LABELS = {
  clientGroup: 'Client Group',
  serviceProvided: 'Service Provided',
  serviceOther: 'Other Service',
  descriptionOfService: 'Description of Service',
  ultimateParent: 'Ultimate Parent',
  overseasOperations: 'Overseas Operations',
  consolidatedFs: 'Consolidated FS',
  isPie: 'Public Interest Entity (PIE)?',
  pieType: 'Type of PIE',
  pieTypeOther: 'Type of PIE (if Others)',
  ...PROFILE_CHANGE_LABELS,
  proposedFees: 'Estimated Fees',
  proposedFeesCcy: 'Estimated Fees Currency',
  ssicPrimary: 'SSIC Code Primary',
  ssicSecondary: 'SSIC Code Secondary',
  principalActivity1: 'Principal Activity 1',
  principalActivity2: 'Principal Activity 2',
  secRegistered: 'SEC Registered',
  tcwgFeeCommunicated: 'Fee Information Communicated to TCWG',
  isGroupAudit: 'Group Audit',
  underInvestigation: 'Under Investigation',
  ultimateParentHqCity: 'Ultimate Parent HQ City',
  ubo: 'UBO',
  isTne: 'Transnational Entity (TNE)',
  tneBasis: 'Basis for the TNE Assessment',
  isTna: 'Transnational Audit (TNA)',
  tnaBasis: 'Basis for the TNA Assessment',
  remarks: 'Remarks',
  entityNames: 'Companies',
};
const CHANGE_LABELS = SCREENING_CHANGE_LABELS;

const SCREENING_DOC_TYPE = 'SCREENING';
const screeningFilesOf = (request) => (request.files || []).filter((fl) => fl.docType === SCREENING_DOC_TYPE);
const supportingFilesOf = (request) => (request.files || []).filter((fl) => fl.docType !== SCREENING_DOC_TYPE);

const WorkflowScreeningDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [flags, setFlags] = useState({});
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [acting, setActing] = useState(false);
  // QM's screening PDF + note — picked next to Complete and posted with it.
  const [screeningUpload, setScreeningUpload] = useState(null);
  const [completionRemarks, setCompletionRemarks] = useState('');

  const load = useCallback(async () => {
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/workflow/screening/${id}`),
        fetchJSON(`/api/workflow/screening/${id}/events`),
      ]);
      setRequest(detail.request);
      setFlags({
        canReview: detail.canReview, canComplete: detail.canComplete, canReject: detail.canReject,
        canModify: detail.canModify, canDelete: detail.canDelete,
      });
      setEvents(hist.events || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const complete = async () => {
    setActing(true); setError(null);
    try {
      const body = new FormData();
      body.append('screening', screeningUpload);
      body.set('completionRemarks', completionRemarks.trim());
      await fetchJSON(`/api/workflow/screening/${id}/complete`, { method: 'POST', body });
      setScreeningUpload(null);
      setCompletionRemarks('');
      setNotice('Screening completed — the document is filed on the screening record.');
      await load();
    } catch (e) { setError(e.message); } finally { setActing(false); }
  };

  const resubmit = async () => {
    setActing(true); setError(null);
    try {
      await fetchJSON(`/api/workflow/screening/${id}/resubmit`, { method: 'POST' });
      setNotice('Re-submitted — QM has been notified.');
      await load();
    } catch (e) { setError(e.message); } finally { setActing(false); }
  };

  const pending = !!request && request.status === 'requested';
  // Opened by an approved Registration or Amendment (a branch).
  const fromRegistration = !!request && request.source !== 'standalone';
  const service = request && (request.serviceProvided === 'Other'
    ? `Other — ${request.serviceOther || ''}` : request?.serviceProvided);

  // 退件的逐項意見：只有單獨發起的單退得了。
  const canComment = !!request && !!flags.canReject;
  const fc = useFieldComments({
    events,
    canComment,
    returned: !!request && request.status === 'awaiting_info',
    labels: CHANGE_LABELS,
  });

  return (
    <RequestDetailShell
      backTo="/workflow/screening"
      backLabel="Screening"
      loading={!request}
      error={error}
      notice={notice}
    >
      {request && (
        <FieldCommentsProvider value={fc}>
          <RequestHeader
            requestNo={request.requestNo}
            typeLabel="Screening"
            badge={<WorkflowStatusBadge status={request.status} />}
            submittedBy={request.requesterEmail}
            submittedAt={request.createdAt}
          />

          <StatusActionsCard
            showDecider
            sides={[{
              key: 'main', label: null, status: request.status,
              note: request.statusUpdatedAt ? formatDateTime(request.statusUpdatedAt) : null,
              approveDisabled: !screeningUpload,
              disabledReason: 'Choose the screening PDF above first',
            }]}
            busy={acting}
            events={events}
            beforeActions={flags.canComplete && pending ? (
              <div className="mt-4 space-y-3">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <div className="min-w-0">
                    <p className={actionPrereqLabel}>
                      Screening Document <span className="text-red-600">*</span>
                    </p>
                    <p className={actionPrereqHint}>Required to complete · PDF, up to 30 MB</p>
                  </div>
                  <FilePicker accept=".pdf" buttonLabel="Choose PDF"
                    onChange={(e) => setScreeningUpload((e.target.files || [])[0] || null)}
                    files={screeningUpload} />
                </div>
                <div>
                  <p className={actionPrereqLabel}>Completion Note</p>
                  <textarea rows="2" value={completionRemarks} onChange={(e) => setCompletionRemarks(e.target.value)}
                    className={`${inputCls} mt-1`} placeholder="Findings, sources checked, anything the record should say." />
                </div>
              </div>
            ) : null}
            onApprove={flags.canComplete && pending ? complete : null}
            approveLabel={() => 'Complete Screening'}
            onReject={canComment && pending ? () => setRejecting(true) : null}
            onModify={flags.canModify ? () => setModifying(true) : null}
            onResubmit={flags.canModify && request.status === 'awaiting_info' ? resubmit : null}
            onDelete={flags.canDelete ? () => setConfirmingDelete(true) : null}
          >
            {request.status === 'completed' && (
              <div>
                <p className={detailLabel}>Completed</p>
                <p className="text-sm text-green-600 mt-1">
                  {request.completedAt ? formatDateTime(request.completedAt) : '—'}
                </p>
              </div>
            )}
            {fromRegistration && pending && !flags.canComplete && (
              <div>
                <p className={detailLabel}>Next Step</p>
                <p className="text-sm text-slate-600 mt-1">QM completes this screening — nothing to do on your side.</p>
              </div>
            )}
          </StatusActionsCard>

          <ChangesSinceReturnCard events={events} changeLabels={CHANGE_LABELS} />

          <SectionCard title="Origin & Companies">
            <DetailGrid>
              <DetailItem label="Origin">
                <span className="inline-flex items-center gap-2">
                  <ScreeningOriginBadge request={request} />
                  {request.sourceRequestNo && <WorkflowRefLink refNo={request.sourceRequestNo} />}
                </span>
              </DetailItem>
              <DetailItem label="Client Group" field="clientGroup"><NowName value={request.clientGroup} now={request.clientGroupNow} /></DetailItem>
              {fromRegistration && (
                <>
                  <ProfileDetailItem request={request} groupKey="service" label="Service Provided" field="serviceProvided">{service || '—'}</ProfileDetailItem>
                  {/* The description belongs to the service, not to the client
                      — same place as on the registration it branched from. */}
                  <ProfileDetailItem request={request} groupKey="descriptionOfService" label="Description of Service" field="descriptionOfService" />
                  <DetailItem label="Estimated Fees" field="proposedFees">{request.proposedFees != null ? `${request.proposedFeesCcy || 'SGD'} ${request.proposedFees.toLocaleString()}` : '—'}</DetailItem>
                </>
              )}
              <div className="sm:col-span-2">
                <DetailItem label={`Companies (${(request.entities || []).length})`} field="entityNames">
                  <ScreeningCompaniesList entities={request.entities || []} />
                </DetailItem>
              </div>
            </DetailGrid>
          </SectionCard>

          {/* A request filed directly asks only the client identity; a branch
              carries the registration's whole profile, shown for QM's context. */}
          <SectionCard title="Client Profile">
            <DetailGrid>
              {/* Each answer reads per company when the companies answered it on their own. */}
              <ProfileDetailItem request={request} groupKey="pie" label="Public Interest Entity (PIE)" field="isPie">{pieText(request) || '—'}</ProfileDetailItem>
              <DetailItem label="Ultimate Parent" field="ultimateParent">{request.ultimateParent || '—'}</DetailItem>
              <ProfileDetailItem request={request} groupKey="ssicPrimary" label="SSIC Code Primary" field="ssicPrimary" />
              <ProfileDetailItem request={request} groupKey="ssicSecondary" label="SSIC Code Secondary" field="ssicSecondary" />
              <ProfileDetailItem request={request} groupKey="principalActivity1" label="Principal Activity 1" field="principalActivity1" />
              <ProfileDetailItem request={request} groupKey="principalActivity2" label="Principal Activity 2" field="principalActivity2" />
              {fromRegistration && (
                <>
                  <ProfileDetailItem request={request} groupKey="overseasOperations" label="Overseas Operations / Overseas Subsidiary" field="overseasOperations" />
                  <ProfileDetailItem request={request} groupKey="consolidatedFs" label="Consolidated Financial Statements" field="consolidatedFs" />
                  <ProfileDetailItem request={request} groupKey="secRegistered" label="Registered with the SEC (US)" field="secRegistered" />
                  <ProfileDetailItem request={request} groupKey="isGroupAudit" label="Group Audit" field="isGroupAudit" />
                  <ProfileDetailItem request={request} groupKey="underInvestigation" label="Under Investigation by Regulatory Authorities" field="underInvestigation" />
                  <ProfileDetailItem request={request} groupKey="tcwgFeeCommunicated" label="Fee Information Communicated to TCWG" field="tcwgFeeCommunicated" />
                </>
              )}
              <DetailItem label="Remarks" field="remarks">{request.remarks || '—'}</DetailItem>
            </DetailGrid>
          </SectionCard>

          <SectionCard title="Screening">
            <ScreeningAnswersView r={request} />
          </SectionCard>

          {/* Only once QM has completed: the filed PDF and the note. While the
              request is pending the upload lives in the status card above, so
              an empty card here would only repeat "nothing yet". */}
          {request.status === 'completed' && (
            <SectionCard title="Screening Document">
              <AttachmentsList
                files={screeningFilesOf(request)}
                downloadHref={(fl) => `${API_BASE_URL}/api/workflow/screening/${request.id}/files/${fl.id}`}
                emptyText="No screening document."
              />
              {request.completionRemarks && (
                <DetailGrid>
                  <DetailItem label="Completion Note">{request.completionRemarks}</DetailItem>
                </DetailGrid>
              )}
            </SectionCard>
          )}

          <SectionCard title="Attachments">
            <AttachmentsList
              files={supportingFilesOf(request)}
              downloadHref={(fl) => `${API_BASE_URL}/api/workflow/screening/${request.id}/files/${fl.id}`}
              emptyText="No supporting files attached."
            />
            <FieldCommentAnchor field="files" label="Attachments" />
          </SectionCard>

          <HistoryCard events={events} changeLabels={CHANGE_LABELS} />

          {rejecting && (
            <RejectModal
              requestNo={request.requestNo}
              fieldComments={fc}
              onClose={() => setRejecting(false)}
              onSubmit={async (reason, fieldComments) => {
                await fetchJSON(`/api/workflow/screening/${request.id}/reject`, {
                  method: 'POST', body: JSON.stringify({ reason, fieldComments }),
                });
                setRejecting(false);
                fc.clear();
                setNotice('Returned to the requester for changes.');
                load();
              }}
            />
          )}
          {modifying && (
            <ScreeningModifyModal
              request={request}
              events={events}
              onClose={() => setModifying(false)}
              onDone={() => {
                setModifying(false);
                setNotice(request.status === 'awaiting_info' ? 'Changes saved. Press Re-submit to send the request back to QM.' : 'Changes saved.');
                load();
              }}
            />
          )}
          {confirmingDelete && (
            <ConfirmDeleteModal
              title={`Delete ${request.requestNo}`}
              message="This permanently deletes the request and its files. This cannot be undone."
              onClose={() => setConfirmingDelete(false)}
              onConfirm={async () => { await fetchJSON(`/api/workflow/screening/${request.id}`, { method: 'DELETE' }); navigate('/workflow/screening'); }}
            />
          )}
        </FieldCommentsProvider>
      )}
    </RequestDetailShell>
  );
};

// Modify modal — the complete form, prefilled (standalone requests only).
const ScreeningModifyModal = ({ request, events = [], onClose, onDone }) => {
  const [options, setOptions] = useState(null);
  const [f, setF] = useState(() => ({
    ...emptyScreeningForm(),
    groupId: request.clientGroupId || '',
    entityIds: (request.entities || []).map((e) => e.entityId).filter(Boolean),
    isPie: request.isPie === true ? 'yes' : request.isPie === false ? 'no' : '',
    pieType: request.isPie === true ? (request.pieType || '') : '',
    pieTypeOther: request.isPie === true ? (request.pieTypeOther || '') : '',
    profileSplit: profileSplitOf(request.entities),
    ultimateParent: request.ultimateParent || '',
    ssicPrimary: request.ssicPrimary || '',
    ssicSecondary: request.ssicSecondary || '',
    principalActivity1: request.principalActivity1 || '',
    principalActivity2: request.principalActivity2 || '',
    ...screeningAnswersToFields(request),
    independenceDeclared: true,
    remarks: request.remarks || '',
  }));
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const { removeIds, toggle: toggleRemove, appendTo } = useRemovableFiles();

  useEffect(() => {
    (async () => {
      try { setOptions(await fetchJSON('/api/workflow/screening/options')); }
      catch (e) { setError(e.message); }
    })();
  }, []);

  const gaps = useMemo(() => screeningRequestGaps(f), [f]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setSaving(true); setError(null);
    try {
      const form = screeningRequestFormData(f, files);
      appendTo(form);
      await fetchJSON(`/api/workflow/screening/${request.id}`, { method: 'PATCH', body: form });
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormModal
      title={`Modify ${request.requestNo}`}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      <ReturnCommentsBanner events={events} />
      <FormValidationProvider value={validation.providerValue}>
        <ScreeningRequestFormFields f={f} set={set} options={options} />
        <div className="border-t border-slate-200 pt-4 space-y-4">
          <div>
            <h4 className={subsectionTitle}>
              <span className={subsectionTitleBar} aria-hidden="true" />
              Attachments
            </h4>
          </div>
          <EditableAttachments
            files={supportingFilesOf(request)}
            removeIds={removeIds}
            onToggle={toggleRemove}
            label="Current Supporting Files"
            downloadHref={(fl) => `${API_BASE_URL}/api/workflow/screening/${request.id}/files/${fl.id}`}
          />
          <FieldCommentInline field="files" />
          <Field label="Add More Supporting Files">
            <FilePicker multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              onChange={(e) => setFiles(Array.from(e.target.files || []))} />
          </Field>
        </div>
        <Field label="Remarks" field="remarks">
          <textarea rows="2" value={f.remarks} onChange={(e) => set('remarks', e.target.value)} className={inputCls} />
        </Field>
      </FormValidationProvider>
      {(error || validation.error) && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <span>{error || validation.error}</span>
        </div>
      )}
    </FormModal>
  );
};

export default WorkflowScreeningDetail;
